import { Component, OnInit, Inject, Input, EventEmitter, Output } from '@angular/core';
import { Deceased } from '../../notify/model/deceased';
import { DisplayDate } from 'app/shared/model/display-date';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { months } from '../../notify/model/date-fields';
import { CustomValidators } from '../validators/CustomValidators';
import { NotifyRepository } from '../../services/repository/notify-repository.service';
import { Router } from '@angular/router';
import { TroveComponent } from '../trove.component';
import { take, takeUntil, debounceTime } from 'rxjs/operators';
 // Needed to avoid Observable.throw is not a function
import * as Raven from 'raven-js';
import { MatDialog } from '@angular/material/dialog';
import { Person } from 'app/notify/model/person';
import { RapidAddressService } from 'app/services/rapid-address.service';
import { LogService } from 'app/services/log.service';
import { DeceasedData } from '../model/deceased-data';
import { Planner } from '../model/planner';

export type DeceasedFormMode = 'living' | 'deceased';

@Component({
  selector: 'app-deceased-form',
  templateUrl: './deceased-form.component.html',
  styleUrls: ['./deceased-form.component.css']
})

export class DeceasedFormComponent extends TroveComponent implements OnInit {

  @Input() deceased: Deceased | Planner;
  @Input() requirePlaces: boolean;
  @Input() requireDateOfDeath: boolean;
  @Input() requestEmailAndPhone: boolean;
  @Input() defaultIdentifier: string;
  @Input() submitLabel: string;
  @Input() mode: DeceasedFormMode = 'deceased'; // Default to deceased for backward compatibility

  @Output() submitDeceased: EventEmitter<DeceasedData> = new EventEmitter();

  protected get isDeceased(): boolean {
    return this.mode === 'deceased' || 'dateOfDeath' in this.deceased;
  }

  submitted = false;
  deceasedForm: UntypedFormGroup;

  showFirstNameAliasField = false;
  showMiddleNameAliasField = false;
  showLastNameAliasField = false;
  
  days = [''];
  months = months;
  birthYears = [''];
  deathYears = [''];

  formErrors = {
    'firstName': '',
    'middleName': '',
    'lastName': '',
    'placeOfBirth': '',
    'placeOfDeath': '',
    'dateOfBirth': '',
    'dateOfDeath': '',
    'fullAddress': '',
    'birthBeforeDeath': ''
  };

  validationMessages = {
    'firstName': {
      'required':      'Please provide this information'
    },
    'lastName': {
      'required':      'Please provide this information'
    },
    'placeOfBirth': {
      'required':      'Please provide this information'
    },
    'placeOfDeath': {
      'required':      'Please provide this information'
    },
    'dateOfBirth': {
      'daterequired':   'Please provide this information',
      'valid':   'Oops, that date doesn\'t exist in the month and year you\'ve picked.',
      'dateinpast': 'Please provide a date in the past.'
    },
    'dateOfDeath': {
      'daterequired':   'Please provide this information',
      'valid':   'Oops, that date doesn\'t exist in the month and year you\'ve picked.',
      'dateinpast': 'Please provide a date in the past.'
    },
    'fullAddress': {
      'required':      'Please provide this information'
    },
    'birthBeforeDeath': 'Please check the entered dates'
  };

  addresses: string[] = [];

  constructor(
    private fb: UntypedFormBuilder,
    private rapidAddressService: RapidAddressService,
    public dialog: MatDialog,
    private logService: LogService
  ) {
    super();
  }

  public ngOnInit() {
    for (let i = 1; i < 32; i++) {
      this.days.push(i.toString());
    }
    const year = new Date().getFullYear();
    for (let i = 0; i < 120; i++) {
      this.birthYears.push((year - i).toString());
    }
    this.deathYears = this.birthYears.slice(0, 21);
    
    // Initialize dates if not provided
    if (!this.deceased.dateOfBirth) {
      this.deceased.dateOfBirth = new DisplayDate();
    }
    if (this.isDeceased && (this.deceased as Deceased).dateOfDeath === undefined) {
      (this.deceased as Deceased).dateOfDeath = new DisplayDate();
    }
    
    this.buildForm();
  }

  public buildForm(): void {
    const dateOfBirthStatus = this.mode === 'living' ? 'exact' : 
      (this.deceased.dateOfBirthStatus || 'exact');

    const formGroup: any = {
      'firstName': [this.deceased.firstName, [Validators.required]],
      'firstNameAlias' : [this.deceased.firstNameAlias],
      'middleName' : [this.deceased.middleName],
      'middleNameAlias' : [this.deceased.middleNameAlias],
      'lastName' : [this.deceased.lastName, [Validators.required]],
      'lastNameAlias' : [this.deceased.lastNameAlias],
      'dateOfBirth' : this.fb.group({
        'day' : [this.deceased.dateOfBirth.day],
        'month' : [this.deceased.dateOfBirth.month],
        'year' : [this.deceased.dateOfBirth.year],
      }, { validator: dateOfBirthStatus === 'unknown' ? [] : Validators.compose([CustomValidators.daterequired, CustomValidators.datevalid, CustomValidators.dateinpast])
      }),
      'dateOfBirthStatus' : [{value: dateOfBirthStatus, disabled: this.mode === 'living'}],
      'placeOfBirth' : [this.deceased.placeOfBirth, this.requirePlaces ? [Validators.required] : []],
      'fullAddress' : [this.getAddress(this.deceased), [Validators.required]], // Handles initialising addresses in old and new format
      'emailAddresses' : [this.deceased.emailAddresses],
      'phoneNumbers' : [this.deceased.phoneNumbers],
      'nzCitizen' : [this.deceased.nzCitizen],
      'nzPermanentResident' : [this.deceased.nzPermanentResident],
      'identifier' : [this.defaultIdentifier || ''],
    };

    // Only add death-related fields if in deceased mode
    if (this.isDeceased && this.mode === 'deceased') {
      const deceased = this.deceased as Deceased;
      formGroup['deathDateApprox'] = [deceased.deathDateApprox];
      formGroup['overseasDeath'] = [deceased.overseasDeath];
      formGroup['dateOfDeath'] = this.fb.group({
        'day' : [deceased.dateOfDeath.day],
        'month' : [deceased.dateOfDeath.month],
        'year' : [deceased.dateOfDeath.year],
      }, { validator: this.requireDateOfDeath ? Validators.compose([CustomValidators.daterequired, CustomValidators.datevalid, CustomValidators.dateinpast]) : Validators.compose([CustomValidators.datevalid, CustomValidators.dateinpast])
      });
      formGroup['placeOfDeath'] = [deceased.placeOfDeath, this.requirePlaces ? [Validators.required] : []];
    }

    const formValidators = this.mode === 'deceased' ? [CustomValidators.birthBeforeDeath] : [];
    this.deceasedForm = this.fb.group(formGroup, { validator: formValidators });

    this.showFirstNameAliasField = this.deceased.firstNameAlias ? true: false;
    this.showMiddleNameAliasField = this.deceased.middleNameAlias ? true: false;
    this.showLastNameAliasField = this.deceased.lastNameAlias ? true: false;

    this.deceasedForm.valueChanges.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(data => this.showValidationErrors(false));

    this.deceasedForm.get('dateOfBirthStatus')
      .valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(data => {
        this.updateOptionality(data as string)
      });

    this.deceasedForm.get('fullAddress')
      .valueChanges
      .pipe(takeUntil(this.ngUnsubscribe), debounceTime(300))
      .subscribe(data => this.updateAddressSuggestions(data as string));

    this.showValidationErrors(false); // (re)set validation messages now
  }

  public getAddress(deceased: Deceased | Planner): string {
    if (deceased.fullAddress) {
      return deceased.fullAddress;
    } else if (deceased.addressLine1) {
      return `${deceased.addressLine1} ${deceased.addressLine2 || ''} ${deceased.addressCity || ''} ${deceased.addressPostCode || ''}`;
    } else {
      return '';
    }
  }
  
  public onSubmit() {
    this.submitted = true;
    this.showValidationErrors(true);
    if (this.deceasedForm.valid) {
      const formValue = this.deceasedForm.value;
      const identifier = formValue.identifier;
      
      if (identifier) {
        formValue.identifier = undefined;
        this.submitDeceased.emit({ deceased: formValue, identifier: identifier });
      } else {
        this.submitDeceased.emit({ deceased: formValue });
      }
    }
  }
  
  public showHint(fieldLabel: string, message: string) {
    this.showMessage(this.dialog, fieldLabel, message, 'GOT IT');
  }

  showValidationErrors(ignoreDirty: boolean) {
    if (!this.deceasedForm) { return; }
    const form = this.deceasedForm;
    
    this.formErrors.birthBeforeDeath = '';
    for (const field of Object.keys(this.formErrors)) {
      // clear previous error message (if any)
      this.formErrors[field] = '';
      const control = form.get(field);

      if (control && (control.dirty || ignoreDirty) && !control.valid) {
        const messages = this.validationMessages[field];
        for (const key of Object.keys(control.errors)) {
          this.formErrors[field] += messages[key] + ' ';
        }
      }
    }
    if (form.errors?.birthBeforeDeath && (form.touched || form.dirty) && !form.valid) {
      this.formErrors.birthBeforeDeath = this.validationMessages.birthBeforeDeath;
    }
  }

  clear() {
    this.deceasedForm.reset();
    this.showFirstNameAliasField = false;
    this.showMiddleNameAliasField = false;
    this.showLastNameAliasField = false;

  }

  updateAddressSuggestions(text: string) {
    if (text && text.length > 4) {
      const matchingAddress = this.addresses.find (address => address === text);
      if (matchingAddress) {
        return;
      }
      this.rapidAddressService.addressSearch(text).pipe(take(1)).subscribe(addresses => {
        this.addresses = addresses.map(address => {
          return address.fullAddress;
        });
      }, err => {
        this.logService.error('Load addresses failed: ' + JSON.stringify(err, null, 4));
        Raven.captureException(err);
      });
    }
    this.addresses = [];
  }

  private updateOptionality(dateOfBirthStatus: string) {
    this.logService.log(`dateOfBirthStatus=${dateOfBirthStatus}`);
    const dateOfBirthControl = this.deceasedForm.get('dateOfBirth');
    if (dateOfBirthStatus === 'exact' || dateOfBirthStatus === 'approximate') {
      dateOfBirthControl.setValidators(Validators.compose([CustomValidators.daterequired, CustomValidators.datevalid, CustomValidators.dateinpast]));
    } else {
      dateOfBirthControl.clearValidators();
    }
    dateOfBirthControl.updateValueAndValidity({ emitEvent: false });

  }

}
